import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Dynamic import of packagesApi to maintain code splitting
// Hook to get available packages based on phone type
export function useAvailablePackages(
  phoneType: "regular" | "tollfree",
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["availablePackages", phoneType],
    queryFn: async () => {
      const { packagesApi } = await import("./packagesApi");
      return packagesApi.getAvailablePackages(phoneType);
    },
    enabled: !!phoneType && options?.enabled !== false, // Only run query when phoneType is provided and enabled is not false
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors (resource not found)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: 1000, // 1 second delay between retries
    ...options,
  });
}

// Hook to get reservation data
export function useReservation() {
  return useQuery({
    queryKey: ["reservation"],
    queryFn: async () => {
      const { packagesApi } = await import("./packagesApi");
      return packagesApi.getReservation();
    },
  });
}

// Hook to update reservation
export function useUpdateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reservationId,
      packageId,
    }: {
      reservationId: string;
      packageId: string;
    }) => {
      const { packagesApi } = await import("./packagesApi");
      return packagesApi.updateReservation(reservationId, packageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservation"] });
    },
  });
}

// Hook to create reservation
export function useCreateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packageId,
      phoneNumber,
    }: {
      packageId: string;
      phoneNumber: string;
    }) => {
      const { packagesApi } = await import("./packagesApi");
      return packagesApi.createReservation(packageId, phoneNumber);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservation"] });
    },
  });
}

// Hook to delete reservation
export function useDeleteReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { packagesApi } = await import("./packagesApi");
      return packagesApi.deleteReservation();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservation"] });
    },
  });
}
